import { useState, type ReactNode } from "react";
import * as Abas from "@radix-ui/react-tabs";
import { Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Botao } from "@/componentes/Botao";
import { Esqueleto } from "@/componentes/Esqueleto";
import { useFocoNoTitulo, useTituloDoDocumento } from "@/componentes/useTituloDoDocumento";
import { quantidade } from "@/formato";
import { rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { ListaDeCedentes, useListaDeCedentes } from "./ListaDeCedentes";
import { ListaDeConvenios, useListaDeConvenios } from "./ListaDeConvenios";
import { NovoCedente } from "./NovoCedente";

const ABAS = [
  ["cedentes", "Cedentes"],
  ["convenios", "Convênios"],
] as const;

/*
 * `/cedentes`: a raiz do cadastro, como na TecnoSpeed (título e Adicionar), abrindo em
 * Cedentes. Convênios é a varredura de todos os cedentes, em `?aba=convenios`.
 *
 * Aba de lista fica na consulta, com `replace` (design D2). As abas dividem `q`, `banco`
 * e `cursor` na URL, e a posição de uma lista não vale na outra: trocar tira o `cursor`.
 * O `status` só existe em Convênios, e sai junto: filtro sem chip é filtro invisível.
 *
 * Adicionar some em Convênios: convênio nasce da conta dele, e nenhuma lista cria um.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A ABA É O TÍTULO À VISTA.                                                │
 * │                                                                          │
 * │ Menu, título e aba diziam "Cedentes" em sequência, e a contagem dizia    │
 * │ pela quarta vez. O `h1` continua, para o leitor de tela e para o foco    │
 * │ da troca de rota. A contagem é só o número, dentro da aba de quem conta. │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function Cedentes() {
  const { pode } = useSessao();
  const navegar = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const [criando, setCriando] = useState(false);
  const titulo = useFocoNoTitulo();
  useTituloDoDocumento("Cedentes");

  const aba = parametros.get("aba") === "convenios" ? "convenios" : "cedentes";

  function trocarDeAba(valor: string) {
    const proximos = new URLSearchParams(parametros);
    proximos.delete("cursor");
    if (valor === "convenios") {
      proximos.set("aba", valor);
    } else {
      proximos.delete("aba");
      proximos.delete("status");
      proximos.delete("certificado");
    }
    setParametros(proximos, { replace: true });
  }

  return (
    <section className="flex flex-col gap-3">
      <h1 ref={titulo} tabIndex={-1} className="sr-only">
        Cedentes
      </h1>

      {criando && (
        <NovoCedente
          aoFechar={() => setCriando(false)}
          aoCriar={(id) => void navegar(rotaDoCedente(id))}
        />
      )}

      <Abas.Root value={aba} onValueChange={trocarDeAba}>
        <header className="flex items-center gap-3 border-b border-fio">
          <Abas.List aria-label="Listas de cedentes" className="flex gap-1">
            {ABAS.map(([valor, rotulo]) => (
              <Abas.Trigger
                key={valor}
                value={valor}
                className="-mb-px inline-flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-titulo font-semibold text-texto-2 hover:text-texto data-[state=active]:border-esferografica data-[state=active]:text-texto"
              >
                {/* O espaço é do leitor de tela: sem ele, ouve-se "Cedentes73". */}
                {rotulo}{" "}
                {valor === aba && CONTAGEM[valor]}
              </Abas.Trigger>
            ))}
          </Abas.List>

          {/* Some para quem não é `admin`: a API recusaria com `403`. */}
          {pode("admin") && aba === "cedentes" && (
            <Botao className="ml-auto" onClick={() => setCriando(true)}>
              <Plus aria-hidden="true" className="size-4" />
              Adicionar
            </Botao>
          )}
        </header>

        <Abas.Content value="cedentes" className="pt-4">
          <ListaDeCedentes />
        </Abas.Content>

        <Abas.Content value="convenios" className="pt-4">
          <ListaDeConvenios />
        </Abas.Content>
      </Abas.Root>
    </section>
  );
}

function ContagemDeCedentes() {
  const { data, isPending } = useListaDeCedentes().consulta;
  return <Contagem carregando={isPending} total={data?.total} />;
}

function ContagemDeConvenios() {
  const { data, isPending } = useListaDeConvenios().consulta;
  return <Contagem carregando={isPending} total={data?.total} />;
}

const CONTAGEM: Record<(typeof ABAS)[number][0], ReactNode> = {
  cedentes: <ContagemDeCedentes />,
  convenios: <ContagemDeConvenios />,
};

/**
 * O total do filtro, e não o da página. Sem leitura, nada: a faixa de erro já diz. O nome
 * da aba diz o que se conta, à vista e para o leitor de tela: `Cedentes 73`.
 */
function Contagem({ carregando, total }: { carregando: boolean; total: number | undefined }) {
  if (carregando) return <Esqueleto className="h-4 w-8" />;
  if (total === undefined) return null;

  return <span className="rotulo tabular">{quantidade(total)}</span>;
}
